import Vue, { type CreateElement } from 'vue';

import VueMeta from 'vue-meta';
import VueVirtualScroller from 'vue-virtual-scroller';

import '@/features';
import 'lazysizes';

import type { Context } from '@/@types/ssr';

import App from '@/SSRApp.vue';

import { getApolloProvider } from '@/graphql/apollo';
import { createI18n } from '@/i18n';
import { createRouter } from '@/routing';
import createStore from '@/store';

import ConstantsProvider from '@/constants/ConstantsProvider.vue';

import {
	initApp,
	initClientStorage,
	initFirebase,
	initLanguage,
	initLocale,
	initRouter,
	initSSR,
	initStore,
	jwIdServerClientHandover,
	refreshToken,
} from '@/main.common';

import { createThumborFilter } from '@/filters/thumbor';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { setVm } from '@/helpers/vm-helper';

import { GlobalComponentsPlugin } from '@/plugins/global-components';
import { GlobalDirectivesPlugin } from '@/plugins/global-directives';
import Jw, { Options as JwOptions } from '@/plugins/jw';
import Ready, { Options as ReadyOptions } from '@/plugins/ready';
import type { ApolloProvider } from 'vue-apollo';
import ListsCountProvider from '@/constants/ListsCountProvider.vue';

Vue.config.ignoredElements = [/^ion-/];
Vue.config.productionTip = process.env.NODE_ENV !== 'production';
Vue.config.performance = process.env.NODE_ENV === 'production';

Vue.use(VueMeta, {});

global.IS_TOUCH = process.client ? !window.matchMedia('(hover: hover)').matches : false;

Vue.use(GlobalComponentsPlugin);
Vue.use(GlobalDirectivesPlugin);

// START: CLS Check
if (process.env.NODE_ENV !== 'production') {
	import('@/plugins/cls-check').then(module => Vue.use(module.ClsCheckPlugin));
}
// END: CLS Check

export default async (context: Context) => {
	// pass context down to App instance and all Components
	Vue.prototype.$context = context;

	await initFirebase();

	/***** SERVER ENV *****/
	if (process.server) {
		const customHeaders = context.ssr.data?.SERVER_ENV?.customHeaders;
		if (customHeaders) {
			const { addCustomHeadersToAxios } = await import('@/helpers/axios-server-helper');
			addCustomHeadersToAxios(customHeaders);
		}
	}
	/***** SERVER ENV *****/

	let localStoragePromise;
	let ionicPromise;
	if (process.client) {
		// Start loading Ionic
		ionicPromise = import('@/ionic.loader');
		localStoragePromise = initClientStorage();
	}

	if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
		// Make basic SSR init before init Store
		initSSR(context);
	}

	await localStoragePromise;
	const store = await createStore(context);
	context.store = store;

	let refreshTokenPromise;
	if (process.client) {
		refreshTokenPromise = refreshToken(context);
	}

	/***** JW PLUGIN *****/
	Vue.use<JwOptions>(Jw);
	/***** READY STORE *****/
	Vue.use<ReadyOptions>(Ready, { store });

	const i18n = createI18n();
	context.i18n = i18n;

	context.webLocale = await initLocale(context);

	const router = createRouter(context.webLocale, store);
	context.router = router;

	// This line MUST come after `await initLocale(context)`
	// It's here because `initLanguage` needs the router to check if we're on a sports route
	context.language = await initLanguage(context);

	Vue.filter('Thumbor', createThumborFilter(context.store.state));

	TrackingHelper.resetState();
	TrackingHelper.setStore(store);

	if (process.client) {
		// jwId handover needs to happen before we await for refreshTokenPromise,
		// because if there is an error with the refresh token, it might await ReadyType.JW_ID endlessly
		await jwIdServerClientHandover(store);
	}

	// wait for the refreshToken promise to finish Apollo init (it needs a fresh access token)
	await refreshTokenPromise;

	let apolloProvider: ApolloProvider<any>;
	if (BUILD_CONFIG.globals.BUILD_TARGET === 'SPA') {
		// If SPA we need to wait for the store to be created to make basic SPA init
		const { initSPA } = await import(/* webpackChunkName: "main-common" */ '@/main.common');
		apolloProvider = await getApolloProvider({
			store,
		});
		await initSPA(context, apolloProvider);
	}
	/**
	 * Backend Tracking
	 * Forward HTTP request user-agent to apollo requests
	 * Used for Tracking Mutations
	 **/
	let extraHeaders = {};
	if (process.server) {
		const UserAgent = context?.req?.headers['user-agent'];
		if (UserAgent && UserAgent.indexOf('GoogleStackdriverMonitoring-UptimeChecks') === -1) {
			extraHeaders = {
				'x-forwarded-user-agent': UserAgent,
			};
		}
	}
	/***** Vue Apollo init *****/
	// @ts-ignore
	if (!apolloProvider) {
		apolloProvider = await getApolloProvider({
			headers: {
				...context.ssr?.data?.SERVER_ENV?.customHeaders,
				...extraHeaders,
			},
			store,
		});
	}
	/***** Vue Apollo init *****/

	/***** SERVER ENV *****/
	if (process.server) {
		// clear SERVER_ENV to avoid leaks
		delete context.ssr.data.SERVER_ENV;
	}
	/***** SERVER ENV *****/
	await initStore(context);

	await initRouter(context);

	await initApp(context);

	if (process.client) {
		await ionicPromise;
		const Ionic = (await ionicPromise)?.default;
		Ionic && Vue.use(Ionic);
	}

	// plugins that need to be used in client and server:
	Vue.use(VueVirtualScroller);

	context.app = new Vue({
		router,
		store,
		i18n,
		apolloProvider,
		render: (h: CreateElement) => h(ConstantsProvider, [h(ListsCountProvider, [h(App)])]),
	});

	setVm(context.app);

	return context;
};
